import React, { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { getToken, getMe } from "../api";
import { RESPONSIVE_NAV_CSS, navToggleProps, navToggleStyle } from "../components/navResponsive";

export default function AdminLayout() {
  // There is one login page. This area is a gate on the session that page
  // created, not a second place to sign in: an anonymous visitor is sent to the
  // login page and a signed-in non-admin back to their own dashboard, so an
  // admin-looking login form can never be the thing that greets either of them.
  const [status, setStatus] = useState(getToken() ? "loading" : "anonymous");
  const [user, setUser] = useState(null);
  const [navOpen, setNavOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getToken()) { setStatus("anonymous"); return; }
    getMe()
      .then((u) => {
        setUser(u);
        setStatus(u.is_admin ? "ok" : "forbidden");
      })
      .catch(() => {
        localStorage.removeItem("token");
        setStatus("anonymous");
      });
  }, []);

  function handleLogout() {
    localStorage.removeItem("token");
    navigate("/", { replace: true });
  }

  if (status === "loading") return null;
  if (status === "anonymous") return <Navigate to="/" replace />;
  if (status === "forbidden") return <Navigate to="/dashboard" replace />;

  return (
    <div style={s.page}>
      <style>{RESPONSIVE_NAV_CSS}</style>

      <nav style={s.nav}>
        <div style={s.navLeft}>
          <button {...navToggleProps(navOpen, setNavOpen)} style={{ ...navToggleStyle, color: "#e2e8f0", borderColor: "#475569" }}>
            {navOpen ? "✕" : "☰"}
          </button>
          <span style={s.logo}>AutoRev</span>
          <span style={s.badge}>Admin</span>
        </div>
        <div style={s.navRight}>
          {/* The counterpart to "Admin Dashboard" on the user dashboard — without it
              the only way back to the app list is editing the URL. */}
          <button onClick={() => navigate("/dashboard")} style={s.exitBtn}>← Dashboard</button>
          <span style={s.userEmail}>{user.email}</span>
          <button onClick={handleLogout} style={s.logoutBtn}>Logout</button>
        </div>
      </nav>

      <div style={s.layout} className="ar-layout">
        <aside
          id="app-sidebar"
          className={navOpen ? "ar-sidebar ar-sidebar-open" : "ar-sidebar"}
          style={s.sidebar}
          onClick={() => setNavOpen(false)}
        >
          <NavLink to="/admin/apps" style={navStyle}>Apps</NavLink>
          <NavLink to="/admin/users" style={navStyle}>Users</NavLink>
          <NavLink to="/admin/redis" style={navStyle}>Redis</NavLink>
        </aside>
        <main style={s.content} className="ar-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function navStyle({ isActive }) {
  return {
    display: "block",
    padding: "10px 16px",
    borderRadius: 8,
    textDecoration: "none",
    fontSize: 14,
    fontWeight: isActive ? 600 : 400,
    color: isActive ? "#1e293b" : "#475569",
    backgroundColor: isActive ? "#e2e8f0" : "transparent",
    marginBottom: 2,
  };
}

const s = {
  page: { minHeight: "100vh", backgroundColor: "#f8fafc", fontFamily: "system-ui, sans-serif" },
  nav: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 24px", backgroundColor: "#1e293b" },
  navLeft: { display: "flex", alignItems: "center", gap: 10 },
  navRight: { display: "flex", alignItems: "center", gap: 14 },
  logo: { fontWeight: 700, fontSize: 18, color: "#fff" },
  badge: { fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#cbd5e1", border: "1px solid #475569", borderRadius: 20, padding: "2px 8px" },
  exitBtn: { padding: "6px 12px", backgroundColor: "transparent", color: "#cbd5e1", border: "1px solid #475569", borderRadius: 6, cursor: "pointer", fontSize: 13, whiteSpace: "nowrap" },
  userEmail: { fontSize: 13, color: "#94a3b8" },
  logoutBtn: { padding: "6px 14px", backgroundColor: "#ef4444", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14 },
  layout: { display: "flex", minHeight: "calc(100vh - 53px)" },
  sidebar: { width: 180, padding: "20px 12px", backgroundColor: "#fff", borderRight: "1px solid #f1f5f9", flexShrink: 0 },
  content: { flex: 1, padding: "32px", overflowY: "auto" },
};
